import React, { useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Navbar from "./Navbar";
import Footer from "./Footer";

const PrivacyPolicy = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Update document title
    document.title = "Privacy Policy - EPC Spark";
  }, []);

  return (
    <>
      <Navbar />
      <div className="privacy-policy-page">
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="privacy-policy-content">
                <h1 className="text-center mb-5">Privacy Policy</h1>

                <div className="last-updated mb-4">
                  <p className="text-muted">
                    <strong>Last Updated:</strong>{" "}
                    {new Date().toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>

                <section className="mb-5">
                  <h2>1. Introduction</h2>
                  <p>
                    EPC Spark ("we," "our," or "us") is committed to protecting
                    your privacy. This Privacy Policy explains how we collect,
                    use, disclose, and safeguard your information when you use
                    our AI-powered platform for domestic energy assessors.
                  </p>
                  <p>
                    By using our services, you agree to the collection and use
                    of information in accordance with this policy.
                  </p>
                </section>

                <section className="mb-5">
                  <h2>2. Information We Collect</h2>

                  <h3>2.1 Personal Information</h3>
                  <p>We may collect the following personal information:</p>
                  <ul>
                    <li>Name and contact information (email, phone number)</li>
                    <li>Professional credentials and qualifications</li>
                    <li>Business information and address</li>
                    <li>Payment and billing information</li>
                    <li>Account credentials and preferences</li>
                  </ul>

                  <h3>2.2 Usage Information</h3>
                  <p>
                    We automatically collect certain information when you use
                    our platform:
                  </p>
                  <ul>
                    <li>
                      Device information (IP address, browser type, operating
                      system)
                    </li>
                    <li>Usage patterns and interactions with our platform</li>
                    <li>Performance data and error logs</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>

                  <h3>2.3 Assessment Data</h3>
                  <p>As an energy assessment platform, we may process:</p>
                  <ul>
                    <li>Property information and assessment data</li>
                    <li>
                      Drawings and documentation created through our tools
                    </li>
                    <li>Client information provided during assessments</li>
                    <li>Generated reports and certificates</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2>3. How We Use Your Information</h2>
                  <p>
                    We use the collected information for the following purposes:
                  </p>
                  <ul>
                    <li>
                      Provide and maintain our AI-powered assessment platform
                    </li>
                    <li>Process payments and manage subscriptions</li>
                    <li>Improve our services and develop new features</li>
                    <li>Provide customer support and technical assistance</li>
                    <li>Send important updates and notifications</li>
                    <li>Comply with legal obligations and regulations</li>
                    <li>Prevent fraud and ensure platform security</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2>4. Information Sharing and Disclosure</h2>
                  <p>
                    We do not sell, trade, or rent your personal information to
                    third parties. We may share your information in the
                    following circumstances:
                  </p>

                  <h3>4.1 Service Providers</h3>
                  <p>
                    We may share information with trusted third-party service
                    providers who assist us in:
                  </p>
                  <ul>
                    <li>Payment processing (Stripe)</li>
                    <li>Cloud hosting and infrastructure (Firebase)</li>
                    <li>Customer support and communication</li>
                    <li>Analytics and performance monitoring</li>
                  </ul>

                  <h3>4.2 Legal Requirements</h3>
                  <p>
                    We may disclose your information if required by law or in
                    response to:
                  </p>
                  <ul>
                    <li>Valid legal requests or court orders</li>
                    <li>
                      Government investigations or regulatory requirements
                    </li>
                    <li>Protection of our rights, property, or safety</li>
                    <li>Prevention of fraud or security threats</li>
                  </ul>

                  <h3>4.3 Business Transfers</h3>
                  <p>
                    In the event of a merger, acquisition, or sale of assets,
                    your information may be transferred as part of the business
                    transaction.
                  </p>
                </section>

                <section className="mb-5">
                  <h2>5. Data Security</h2>
                  <p>
                    We implement appropriate technical and organizational
                    measures to protect your information:
                  </p>
                  <ul>
                    <li>Encryption of data in transit and at rest</li>
                    <li>Regular security assessments and updates</li>
                    <li>Access controls and authentication measures</li>
                    <li>Secure hosting infrastructure</li>
                    <li>Employee training on data protection</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2>6. Data Retention</h2>
                  <p>We retain your information for as long as necessary to:</p>
                  <ul>
                    <li>Provide our services to you</li>
                    <li>Comply with legal obligations</li>
                    <li>Resolve disputes and enforce agreements</li>
                    <li>Improve our services</li>
                  </ul>
                  <p>
                    You may request deletion of your account and associated data
                    at any time.
                  </p>
                </section>

                <section className="mb-5">
                  <h2>7. Your Rights and Choices</h2>
                  <p>
                    You have the following rights regarding your personal
                    information:
                  </p>
                  <ul>
                    <li>
                      <strong>Access:</strong> Request a copy of your personal
                      information
                    </li>
                    <li>
                      <strong>Correction:</strong> Update or correct inaccurate
                      information
                    </li>
                    <li>
                      <strong>Deletion:</strong> Request deletion of your
                      personal information
                    </li>
                    <li>
                      <strong>Portability:</strong> Request transfer of your
                      data to another service
                    </li>
                    <li>
                      <strong>Objection:</strong> Object to certain processing
                      activities
                    </li>
                    <li>
                      <strong>Withdrawal:</strong> Withdraw consent for data
                      processing
                    </li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2>8. Cookies and Tracking Technologies</h2>
                  <p>We use cookies and similar technologies to:</p>
                  <ul>
                    <li>Remember your preferences and settings</li>
                    <li>Analyze platform usage and performance</li>
                    <li>Provide personalized content and features</li>
                    <li>Ensure platform security and functionality</li>
                  </ul>
                  <p>
                    You can control cookie settings through your browser
                    preferences.
                  </p>
                </section>

                <section className="mb-5">
                  <h2>9. International Data Transfers</h2>
                  <p>
                    Your information may be transferred to and processed in
                    countries other than your own. We ensure appropriate
                    safeguards are in place to protect your information in
                    accordance with applicable data protection laws.
                  </p>
                </section>

                <section className="mb-5">
                  <h2>10. Children's Privacy</h2>
                  <p>
                    Our services are not intended for children under 16 years of
                    age. We do not knowingly collect personal information from
                    children under 16. If you believe we have collected such
                    information, please contact us immediately.
                  </p>
                </section>

                <section className="mb-5">
                  <h2>11. Changes to This Privacy Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. We will
                    notify you of any material changes by:
                  </p>
                  <ul>
                    <li>Posting the updated policy on our platform</li>
                    <li>Sending email notifications to registered users</li>
                    <li>Displaying prominent notices on our website</li>
                  </ul>
                  <p>
                    Your continued use of our services after such changes
                    constitutes acceptance of the updated policy.
                  </p>
                </section>

                <section className="mb-5">
                  <h2>12. Contact Us</h2>
                  <p>
                    If you have any questions about this Privacy Policy or our
                    data practices, please contact us:
                  </p>
                  <div className="contact-info">
                    <p>
                      <strong>Email:</strong> privacy@epcspark.com
                    </p>
                    <p>
                      <strong>Address:</strong> [Your Business Address]
                    </p>
                    <p>
                      <strong>Phone:</strong> [Your Phone Number]
                    </p>
                  </div>
                </section>

                <section className="mb-5">
                  <h2>13. Governing Law</h2>
                  <p>
                    This Privacy Policy is governed by and construed in
                    accordance with the laws of [Your Jurisdiction]. Any
                    disputes arising from this policy will be subject to the
                    exclusive jurisdiction of the courts in [Your Jurisdiction].
                  </p>
                </section>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
